import * as React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./TurnrDropdownSelect.module.scss";
import classnames from "classnames";
import lodashIsNil from "lodash/isNil";
import _ from "lodash";
import { COLORS } from "~/constants";
import fuzzysort from "fuzzysort";
import lodashDebounce from "lodash/debounce";
import { isInputValueChanged } from "../../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { Tooltip, TurnrTooltipPortalAnchor } from "../tooltip/Tooltip";
import { FixedDropdown } from "../fixedPosition/FixedPositioningAroundElement"; // import TurnrCloseIconButton from "../buttons/icon-buttons/TurnrCloseIconButton";
import useKeyDownDetector, { KEY_NAME_DOWN, KEY_NAME_ENTER, KEY_NAME_ESC, KEY_NAME_SPACE, KEY_NAME_TAB, KEY_NAME_UP } from "rylak-common/src/hooks/useKeyDownDetector.jsx";
import { LoadingComponent } from "rylak-common/src/components/loadingComponent/LoadingComponent.jsx";
import useTurnrCallbackRef from "rylak-common/src/hooks/useTurnrCallbackRef.jsx";
import RoundInfoIcon from "backoffice/src/components/icons/RoundInfoIcon";

const NO_OPTION_VALUE = "NO_OPTION_VALUE";

const OptionToComponent = memo(function OptionToComponent(props) {
    const { option, renderOption } = props;
    let c = (option?.renderFn && option?.renderFn(option)) || (renderOption && renderOption(option)) || option?.content || option?.name || option?.title || option?.label;
    if (c) {
        return c;
    }
    if (!_.isNil(option?.value)) {
        return option?.value;
    }
    if (!_.isNil(option?.id)) {
        return option?.id;
    }
    return option;
});

function PlaceHolderComponent({ placeholder }) {
    return <div className={styles["placeholder"]}>{placeholder}</div>;
}

const Option = memo(function Option(props) {
    const { optionId, dataTestId, option, renderOption, onClick, isSelected, isKeyNavigatedTo, triggerNavigatedTo, optionsRef, multiple, optionDisabled } = props;
    const scollToRef = useRef();

    useEffect(() => {
        if (isKeyNavigatedTo && triggerNavigatedTo && scollToRef.current) {
            if (scollToRef.current.getBoundingClientRect().bottom > optionsRef.current.getBoundingClientRect().bottom) {
                scollToRef.current.scrollIntoView(false);
            }

            if (scollToRef.current.getBoundingClientRect().top < optionsRef.current.getBoundingClientRect().top) {
                scollToRef.current.scrollIntoView();
            }
        }
    }, [isKeyNavigatedTo, triggerNavigatedTo, scollToRef]);

    const localOnClick = useCallback(
        (e) => {
            onClick(e, option);
        },
        [option, onClick],
    );

    const optionIsDisabled = optionDisabled && optionDisabled(option);
    // const optionIsDisabled = true;

    const content = (
        <div
            ref={scollToRef}
            tabIndex={"0"}
            key={optionId}
            onClick={optionIsDisabled ? undefined : localOnClick}
            data-test-id={dataTestId ? dataTestId + "_" + optionId : optionId}
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "relative",
                ...option?.style,
            }}
            className={classnames(
                styles["option"],
                isSelected && styles["selected-option"],
                isKeyNavigatedTo && styles["key-navigated-option"],
                optionIsDisabled && styles["option-disabled"],
                option?.className,
            )}
        >
            <OptionToComponent option={option} renderOption={renderOption} disabled={optionIsDisabled} />
            {multiple && option?.selectable !== false && (
                <div
                    style={{
                        visibility: !isSelected && "hidden",
                        marginLeft: "10px",
                    }}
                >
                    <FontAwesomeIcon color={COLORS.MID_BROWN} icon={faCheck} />
                </div>
            )}
        </div>
    );

    if (option?.tooltip) {
        return (
            <Tooltip content={option?.tooltip} placement={"right"}>
                {content}
            </Tooltip>
        );
    }

    return content;
});

const debouncedResetSearchString = lodashDebounce((setSearchString) => {
    setSearchString("");
}, 600);

function TurnrDropdownSelect(props) {
    const {
        value,
        onChange,
        multiple,
        label,
        color,
        title = label,
        error,
        originalValue,
        options,
        optionDisabled,
        renderOption,
        renderSelected, // Render each selected option in value. If multiple is false. Then renderValue and renderSelected is the same
        renderSelectOverride,
        renderValue, // Rendering the entire value
        getSearchKey,
        disabled,
        fullWidth,
        hideArrow = false,
        labelLocked = true,
        selectNoneComponent,
        fieldsetStyle,
        style,
        className,
        selectedClassName,
        noLeftBox = false,
        size = "small",
        onClick,
        tooltipTitle = title,
        tooltip,
        required,
        tooltipPlacement = "bottom-left",
        name = title,
        placeholder,
        loading,
        OptionsComponent,
        optionsComponentProps,
        renderInput,
        onClose,
        dataTestId,
        arrowSize = "13px",
    } = props;
    const [open, setOpen] = useState();
    const optionsRef = useTurnrCallbackRef();
    const selectRef = React.useRef(null);
    const [navigatedToIndex, setNavigatedToIndex] = useState(-1);
    const [searchString, setSearchString] = useState("");
    const [searchKeys, setSearchKeys] = useState([]);
    const [optionClicked, setOptionClicked] = useState(null);
    const [firstOpen, setFirstOpen] = useState(false); // Special performance boost. Do not render options until first open

    useEffect(() => {
        if (firstOpen && !open) {
            onClose && onClose();
        }
    }, [firstOpen, open]);

    const isOptionSelected = useCallback(
        (optionId) => {
            return multiple ? value.includes(optionId) || (optionId === NO_OPTION_VALUE && value.length === 0) : value === optionId;
        },
        [multiple, value],
    );

    const someOptionIsSelected = useMemo(() => {
        return multiple ? _.isNil(value) || value?.length === 0 : _.isNil(value);
    }, [value, multiple]);

    const computedOptions = useMemo(() => {
        if (!options) {
            return null;
        }
        return selectNoneComponent
            ? [
                  {
                      id: NO_OPTION_VALUE,
                      searchKey: null,
                      renderFn: () => selectNoneComponent,
                  },
              ].concat(options)
            : options;
    }, [selectNoneComponent, options]);

    useEffect(() => {
        if (searchKeys && computedOptions && searchString) {
            const sortResults = fuzzysort.go(searchString, searchKeys);
            if (sortResults?.length) {
                const topPic = sortResults[0]?.target;
                const topPicIndex = topPic && searchKeys.findIndex((key) => key === topPic);
                setNavigatedToIndex(topPicIndex);
            }
        }
    }, [computedOptions, searchKeys, searchString]);

    useEffect(() => {
        setSearchKeys(
            computedOptions?.map((option) => {
                if (_.isObject(option)) {
                    return option.searchKey || (getSearchKey ? getSearchKey(option) : null);
                } else {
                    return getSearchKey ? getSearchKey(option) : option;
                }
            }),
        );
    }, [computedOptions]);

    useEffect(() => {
        debouncedResetSearchString(setSearchString);
    }, [searchString, setSearchString]);

    const tooltipPortalRef = useRef();

    const onOptionClick = useCallback(
        (option) => {
            if (disabled) {
                return;
            }
            const optionId = optionToOptionId(option);
            if (option?.onClick) {
                option.onClick({ setOpen });
                return;
            }
            if (multiple) {
                if (optionId === NO_OPTION_VALUE) {
                    onChange([]);
                    // setOpen(false);
                } else if (value.includes(optionId)) {
                    onChange(value.filter((v) => v !== optionId));
                } else {
                    onChange(value.concat(optionId));
                }
                setNavigatedToIndex(optionIdToIndex(optionId));
            } else {
                if (optionId === NO_OPTION_VALUE) {
                    onChange(undefined);
                } else {
                    onChange(optionId);
                }
                setOpen(false);
            }
        },
        [setOpen, onChange, disabled, value, multiple],
    );

    useEffect(() => {
        if (optionClicked !== null) {
            onOptionClick(optionClicked);
            setOptionClicked(null);
        }
    }, [onOptionClick, optionClicked]);

    const localOnOptionClicked = useCallback((e, option) => {
        e.stopPropagation();
        setOptionClicked(option);
    }, []);

    const toggleOpen = useCallback(
        (pleaseOpen) => {
            if (pleaseOpen && (disabled || loading)) {
                return;
            }
            if (pleaseOpen) {
                if (!firstOpen) {
                    setFirstOpen(true);
                } else {
                    setOpen(true);
                }
            } else {
                setOpen(false);
            }
        },
        [disabled, setOpen, firstOpen],
    );

    useEffect(() => {
        if (firstOpen) {
            setOpen(true);
        }
    }, [firstOpen, setOpen]);

    const valueExists = !lodashIsNil(value) && value !== "";

    const isSelectNoneOptionSelected = useMemo(() => {
        return selectNoneComponent && (_.isNil(value) || value === "" || (multiple && value?.length === 0));
    }, [selectNoneComponent, value]);

    const actualValue = useMemo(() => {
        return isSelectNoneOptionSelected ? NO_OPTION_VALUE : value;
    }, [isSelectNoneOptionSelected, value]);

    const optionToOptionId = useCallback((option) => {
        if (!_.isNil(option?.id)) {
            return option?.id;
        }
        if (!_.isNil(option?.value)) {
            return option?.value;
        }
        return option;
    }, []);

    useEffect(() => {
        if (!open) {
            if (!someOptionIsSelected) {
                setNavigatedToIndex(-1);
            }
            if (multiple) {
                setNavigatedToIndex(computedOptions?.findIndex((o) => optionToOptionId(o) === value[value.length - 1]));
            } else {
                setNavigatedToIndex(computedOptions?.findIndex((o) => optionToOptionId(o) === value));
            }
        }
    }, [open, computedOptions, multiple, value, someOptionIsSelected, optionToOptionId]);

    const computedColor = disabled || loading ? COLORS.textGray : color;

    useKeyDownDetector((event) => {
        if (window.document.activeElement !== selectRef.current) {
            return;
        }
        if (event.key === KEY_NAME_ESC) {
            toggleOpen(false);
        } else if (event.key === KEY_NAME_TAB) {
            toggleOpen(false);
        } else if (event.key === KEY_NAME_SPACE) {
            if (window.document.activeElement === selectRef.current) {
                toggleOpen(true);
            }
        } else if (event.key === KEY_NAME_DOWN) {
            if (open) {
                event.preventDefault();
                if (navigatedToIndex >= computedOptions.length) {
                    return;
                }

                let nextIndex = navigatedToIndex + 1;
                let isDisabled = optionDisabled && optionDisabled(options[nextIndex]);
                while (nextIndex < computedOptions.length - 1 && isDisabled) {
                    nextIndex += 1;
                    isDisabled = optionDisabled && optionDisabled(options[nextIndex]);
                }
                if (!isDisabled && nextIndex <= computedOptions.length - 1) {
                    setNavigatedToIndex(Math.min(computedOptions.length - 1, nextIndex));
                }
            }
        } else if (event.key === KEY_NAME_UP) {
            if (open) {
                event.preventDefault();
                if (navigatedToIndex <= 0) {
                    return;
                }

                let nextIndex = navigatedToIndex - 1;
                let isDisabled = optionDisabled && optionDisabled(options[nextIndex]);
                while (nextIndex > 0 && isDisabled) {
                    nextIndex -= 1;
                    isDisabled = optionDisabled && optionDisabled(options[nextIndex]);
                }
                if (!isDisabled && nextIndex >= 0) {
                    setNavigatedToIndex(Math.max(0, nextIndex));
                }
            }
        } else if (event.key === KEY_NAME_ENTER) {
            event.preventDefault();
            if (open) {
                onOptionClick(indexToOption(navigatedToIndex));
            } else {
                if (window.document.activeElement === selectRef.current) {
                    toggleOpen(true);
                }
            }
        } else {
            if (open) {
                const key = event.key;
                if (key.length === 1 || _.isNumber(key)) {
                    if (/[A-Za-zåäöÅÄÖ0-9-_.,#€%&/()=]/.test(key)) {
                        setSearchString(searchString + key);
                    }
                }
            }
        }
    });

    const indexToOption = useCallback(
        (index) => {
            const result = computedOptions[index];
            return result;
        },
        [computedOptions],
    );

    const optionIdToIndex = useCallback(
        (optionId) => {
            const result = computedOptions.findIndex((option) => option === optionId);
            return result;
        },
        [computedOptions],
    );

    const optionIdToOption = useCallback(
        (optionId) => {
            return computedOptions?.find((option) => optionToOptionId(option) === optionId) || optionId;
        },
        [computedOptions, optionToOptionId],
    );

    const optionToComponent = useCallback(
        (option) => {
            return <OptionToComponent option={option} renderOption={renderOption} optionDisabled={optionDisabled} />;
        },
        [renderOption],
    );

    const selectedOptionToComponent = useCallback(() => {
        if (loading) {
            return <LoadingComponent />;
        }
        if (multiple) {
            if (isSelectNoneOptionSelected) {
                return selectNoneComponent;
            }
            if (!value?.length && placeholder) {
                return <PlaceHolderComponent placeholder={placeholder} />;
            }
            if (renderValue) {
                return renderValue(value);
            }
            if (!value?.length) {
                return null;
            }
            if (value?.length > 3) {
                return <div>{value.length} items selected</div>;
            }
            return (
                <div style={{ display: "flex" }}>
                    {value.map((v, index) => {
                        const option = optionIdToOption(v);
                        return (
                            <div key={index} style={{ display: "flex" }}>
                                {index !== 0 && <span>,&nbsp;</span>}
                                {renderSelected ? renderSelected(option) : optionToComponent(option)}
                            </div>
                        );
                    })}
                </div>
            );
        }
        const option = optionIdToOption(value);
        if (isSelectNoneOptionSelected) {
            return selectNoneComponent;
        }
        if (_.isNil(value)) {
            return <PlaceHolderComponent placeholder={placeholder} />;
        }
        return renderValue ? renderValue(option) : renderSelected ? renderSelected(option) : optionToComponent(option);
    }, [renderSelected, optionToComponent, computedOptions, isSelectNoneOptionSelected, multiple, value, placeholder, loading]);

    const hasOriginalValue = useMemo(() => {
        return originalValue !== undefined;
    }, [originalValue]);

    const valueChanged = useMemo(() => {
        if (!hasOriginalValue) {
            return false;
        }

        return isInputValueChanged(originalValue, value);
    }, [hasOriginalValue, value, originalValue, multiple]);

    return (
        <TurnrTooltipPortalAnchor ref={tooltipPortalRef} placement={tooltipPlacement} style={{ width: fullWidth && "100%" }}>
            <div
                tabIndex={disabled || loading ? "-1" : "0"}
                ref={selectRef}
                data-test-id={dataTestId}
                onClick={(e) => {
                    onClick && onClick(e);
                    toggleOpen(true);
                }}
                className={classnames(
                    styles["custom-select"],
                    open && styles["open"],
                    error && styles["error"],
                    (disabled || loading) && styles["disabled"],
                    valueChanged && styles["value-changed"],
                    styles["size-" + size],
                    className,
                )}
                style={{ width: fullWidth && "100%", ...style }}
            >
                <select
                    // readOnly
                    tabIndex={"-1"}
                    data-has-value={valueExists}
                    value={actualValue}
                    disabled={disabled || loading}
                    multiple={multiple}
                    required={required}
                    onChange={() => {
                        // we handle this elsewhere
                    }}
                    name={name}
                    className={styles["select"]}
                >
                    <option />
                    {computedOptions?.map((option) => {
                        const id = optionToOptionId(option);
                        return (
                            <option key={id} value={id}>
                                {id}
                            </option>
                        );
                    })}
                </select>
                {renderInput ? (
                    renderInput()
                ) : (
                    <>
                        <div className={classnames(styles["select-selected"], selectedClassName, open && styles["open"], hideArrow && styles["hide-arrow"])} style={{ color: computedColor }}>
                            {renderSelectOverride ? renderSelectOverride() : selectedOptionToComponent()}
                        </div>

                        {title && (
                            <label className={classnames(styles["floating-label"], labelLocked && styles["label-locked"])}>
                                {title} {required && "*"}
                            </label>
                        )}
                        <fieldset className={classnames(styles["fieldset"], !title && styles["no-title"])} style={fieldsetStyle}>
                            {title && (
                                <legend className={classnames(styles["legend"])}>
                                    <span>
                                        {title} {required && "*"}
                                    </span>
                                </legend>
                            )}
                        </fieldset>
                        {!hideArrow && (
                            <div className={classnames(styles["arrow"], tooltip && styles["arrow-with-tooltip"])}>
                                {/*{multiple &&*/}
                                {/*<div style={{visibility: value?.length === 0 && "hidden"}} className={styles["close-btn"]}>*/}
                                {/*    <TurnrCloseIconButton disabled={disabled} onClick={(e) => {*/}
                                {/*        e.stopPropagation();*/}
                                {/*        e.preventDefault();*/}
                                {/*        onChange([], e);*/}
                                {/*    }}/></div>*/}
                                {/*}*/}
                                <FontAwesomeIcon
                                    className={styles["arrow-icon"]}
                                    icon={faChevronDown}
                                    style={{
                                        width: arrowSize || "13px",
                                        height: arrowSize || "13px",
                                        color: color,
                                    }}
                                    fixedWidth={true}
                                />
                            </div>
                        )}

                        {tooltip && (
                            <Tooltip
                                portalRef={tooltipPortalRef}
                                content={tooltip}
                                title={tooltipTitle}
                                style={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    marginRight: "5px",
                                }}
                            >
                                <RoundInfoIcon />
                            </Tooltip>
                        )}
                        {!noLeftBox && <div className={styles["value-changed-box"]} style={{ backgroundColor: computedColor }} />}
                    </>
                )}

                <FixedDropdown elementRef={selectRef} dropdownRef={optionsRef} open={open} setOpen={toggleOpen} placement={tooltipPlacement}>
                    <div className={styles["options"]}>
                        {firstOpen && (
                            <>
                                {OptionsComponent ? (
                                    <OptionsComponent {...optionsComponentProps} />
                                ) : (
                                    computedOptions?.map((option, index) => {
                                        const optionId = optionToOptionId(option);

                                        const isKeyNavigatedTo = navigatedToIndex === index;

                                        return (
                                            <Option
                                                optionsRef={optionsRef}
                                                key={optionId}
                                                dataTestId={dataTestId}
                                                isKeyNavigatedTo={isKeyNavigatedTo}
                                                triggerNavigatedTo={isKeyNavigatedTo && open}
                                                optionId={optionId}
                                                option={option}
                                                renderOption={renderOption}
                                                isSelected={isOptionSelected(optionId)}
                                                color={computedColor}
                                                multiple={multiple}
                                                onClick={localOnOptionClicked}
                                                optionDisabled={optionDisabled}
                                            />
                                        );
                                    })
                                )}
                            </>
                        )}
                    </div>
                </FixedDropdown>
            </div>
        </TurnrTooltipPortalAnchor>
    );
}

export default TurnrDropdownSelect;
